import { useEffect } from 'react';

import { useEmail } from '@/components/providers/EmailProvider/EmailProvider';
import usePendingConceptReplyStore from '@/features/compose/stores/usePendingConceptReplyStore';
import { bodyContainsDraftMarker } from '@/features/email/outlook/draftMarkers';
import getBody from '@/features/email/outlook/getBody';
import isComposeMode from '@/features/email/outlook/isComposeMode';
import useLocalSettings from '@/stores/useLocalSettings';

// Module-scoped so auto-generation fires at most once per message per
// taskpane session: generating navigates away from this hook's component, and
// coming back must not retrigger (and re-navigate) in a loop.
const autoFiredMessageIds = new Set<string>();

export default function useAutoGenerateConceptReply(
  generate: (source: 'auto-generate') => Promise<unknown> | unknown,
): void {
  const isPending = usePendingConceptReplyStore((s) => s.isPending);
  const consume = usePendingConceptReplyStore((s) => s.actions.consume);
  const autoGenerateConceptReply = useLocalSettings(
    (s) => s.autoGenerateConceptReply,
  );
  const {
    email,
    analysis: { analysis },
  } = useEmail();
  const messageId = email?.messageId ?? '';

  useEffect(() => {
    const inReplyTo = Office.context.mailbox?.item?.inReplyTo;
    const settingRequestsGeneration =
      autoGenerateConceptReply && isComposeMode() && !!inReplyTo;

    if (!isPending && !settingRequestsGeneration) return;
    if (autoFiredMessageIds.has(messageId)) return;
    if (!analysis?.email_id) return;

    autoFiredMessageIds.add(messageId);

    const fire = async () => {
      if (isPending) consume();

      if (!isPending) {
        const body = await getBody('Html', { hostConfig: true });
        if (bodyContainsDraftMarker(body)) return;
      }

      await generate('auto-generate');
    };

    void fire();
  }, [
    isPending,
    autoGenerateConceptReply,
    analysis?.email_id,
    messageId,
    generate,
    consume,
  ]);
}
